import {
    IPrPayslipEmploymentItemEntity,
    IPrPayslipEmploymentSubitemEntity,
    IPrPayslipItemEntity,
    PrPayslipItemEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrPayslipEmploymentItemTypeCode,
    PrPayslipEmploymentSubitemTypeCode,
    PrPayslipItemTypeCode,
    PrPayslipSubitemTypeCode
} from "@odata/GeneratedEnums";
import { TFormTableListValue } from "@pages/payroll/payslip/formTableList/FormTableList";
import {
    RowValueType,
    TFilterSubitemsFn,
    TPayslipItem,
    TPayslipOverviewFormatterFn,
    TPayslipOverviewRowDef,
    TPayslipSubItemDef
} from "@pages/payroll/payslip/overview/TotalOverview.utils";
import i18next from "i18next";

import { DASH_CHARACTER } from "../../../../constants";
import { TRecordAny } from "../../../../global.types";
import DateType, { DateFormat, getUtcDayjs } from "../../../../types/Date";

interface IPayslipOverviewSectionsDef {
    title?: string;
    rows: TPayslipOverviewRowDef[];
}

function getRowHeaderLabel(key: string): string {
    return `${i18next.t(`Payslip:FormOverviews.${key}`).toString()}:`;
}

function getHeaderRowDef(additionalLabels: string[], visibilityCondition?: string[]): TPayslipOverviewRowDef {
    return {
        label: getRowHeaderLabel("Name"),
        value: getRowHeaderLabel("Amount"),
        isHeader: true,
        isSubRow: true,
        subItems: additionalLabels.map((key): TPayslipSubItemDef => ({ value: getRowHeaderLabel(key) })),
        visibleIfContains: visibilityCondition
    };
}


const SALE_SUBITEM_ID = "Sale";
const filterOutSaleSubitemsIfNotPresent: TFilterSubitemsFn = (defs, args) => {
    const hasSaleSubitems = args.filteredItems?.some(item => {
        return (item as TPayslipItem).Subitems?.some(sub => [PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployerDiscountPerc, PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeeRetireeDiscPerc].includes(sub.TypeCode as PrPayslipEmploymentSubitemTypeCode));
    });
    if (hasSaleSubitems) {
        return defs;
    }
    return defs?.filter(def => {
        return def?.id !== PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployerDiscountPerc &&
            def?.id !== PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeeRetireeDiscPerc &&
            def?.id !== SALE_SUBITEM_ID;
    });
};

const deductionTypeFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter, _, args) => {
    return (item as IPrPayslipItemEntity)?.Deduction?.Type?.Name;
};

/**
 * Configuration for basic payslip calculations block related to employee
 */
export const getTotalOverviewPayslipSectionsDef: () => IPayslipOverviewSectionsDef[] = () => ([
    {
        rows: [
            // **** Basic calculations block ****
            { id: PrPayslipItemTypeCode.TaxableIncomeTotal, isHeader: true },
            { id: PrPayslipItemTypeCode.AdvanceIncomeTaxCalculationBasis },
            { id: PrPayslipItemTypeCode.WithholdingIncomeTaxCalculationBasis },
            { id: PrPayslipItemTypeCode.AdvanceIncomeTaxCalculationBasisRounded },
            { id: PrPayslipItemTypeCode.AdvanceIncomeTaxBeforeAdvantages },
            { id: PrPayslipItemTypeCode.IncomeTaxReduction },
            { id: PrPayslipItemTypeCode.IncomeTaxAdvantages },
            { id: PrPayslipItemTypeCode.IncomeTaxNet },
            { id: PrPayslipItemTypeCode.IncomeTaxBonus },
            { id: PrPayslipItemTypeCode.WithholdingTax },
            { id: PrPayslipItemTypeCode.AnnualIncomeTaxSettlement }, // TODO: ask PM about placement

            // **** Health Insurance ****
            { id: PrPayslipItemTypeCode.HealthInsuranceTotal, isHeader: true },
            getHeaderRowDef(["Size"], [PrPayslipItemTypeCode.HealthInsurancePaidByEmployee, PrPayslipItemTypeCode.HealthInsurancePaidByEmployer,
                PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployee, PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployer]),
            { id: PrPayslipItemTypeCode.BasisForHealthInsuranceCalculation, isSubRow: true },
            {
                id: PrPayslipItemTypeCode.HealthInsurancePaidByEmployee,
                subItems: [
                    {
                        id: PrPayslipSubitemTypeCode.HealthInsuranceEmployeePercentage,
                        valueType: RowValueType.Percentage
                    }
                ]
            },
            {
                id: PrPayslipItemTypeCode.HealthInsurancePaidByEmployer,
                subItems: [
                    {
                        id: PrPayslipSubitemTypeCode.HealthInsuranceEmployerPercentage,
                        valueType: RowValueType.Percentage
                    }
                ]
            },
            { id: PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployee, isSubRow: true },
            { id: PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployer, isSubRow: true },

            // **** Social Insurance ****
            {
                id: PrPayslipItemTypeCode.SocialInsuranceTotal,
                isHeader: true,
                isVisible: ({ storage }) => {
                    return storage?.data.entity.Employments?.length > 1;
                }
            },

            // **** Net Salary ****
            { id: PrPayslipItemTypeCode.NetSalary, isHeader: true },

            // **** Deductions ****
            { id: PrPayslipItemTypeCode.DeductionTotal, isHeader: true },
            getHeaderRowDef([PrPayslipItemEntity.Type], [PrPayslipItemTypeCode.CustomDeduction, PrPayslipItemTypeCode.OwnDeduction, PrPayslipItemTypeCode.CourtOrderedDeduction,
                PrPayslipItemTypeCode.OwedRegularChildSupport, PrPayslipItemTypeCode.InsolvencyAdministratorReward]),
            {
                id: PrPayslipItemTypeCode.CustomDeduction,
                isSubRow: true,
                subItems: [{ id: PrPayslipItemEntity.TypeCode, formatter: deductionTypeFormatter }]
            },
            {
                id: PrPayslipItemTypeCode.OwnDeduction,
                isSubRow: true,
                subItems: [{ id: PrPayslipItemEntity.TypeCode, formatter: deductionTypeFormatter }]
            },
            {
                id: PrPayslipItemTypeCode.CourtOrderedDeduction,
                isSubRow: true,
                subItems: [{ id: PrPayslipItemEntity.TypeCode, formatter: deductionTypeFormatter }]
            },
            {
                id: PrPayslipItemTypeCode.OwedRegularChildSupport,
                isSubRow: true,
                subItems: [{ id: PrPayslipItemEntity.TypeCode, formatter: deductionTypeFormatter }]
            },
            {
                id: PrPayslipItemTypeCode.InsolvencyAdministratorReward,
                isSubRow: true,
                subItems: [{ id: PrPayslipItemEntity.TypeCode, formatter: deductionTypeFormatter }]
            },

            { id: PrPayslipItemTypeCode.IncomeTaxBonus, isHeader: true },

            { id: PrPayslipItemTypeCode.SalaryAdvanceTotal, isHeader: true },
            {
                id: PrPayslipItemTypeCode.SalaryAdvance,
                isSubRow: true,
                headerRow: getHeaderRowDef([]),
                label: (item) => (item as IPrPayslipItemEntity).SalaryAdvance?.NumberOurs,
            },
            // **** Liabilities and Receivables ****
            { id: PrPayslipItemTypeCode.LiabilitiesTotal, isHeader: true },
            { id: PrPayslipItemTypeCode.Liabilities },
            { id: PrPayslipItemTypeCode.ReceivablesTotal, isHeader: true },
            { id: PrPayslipItemTypeCode.Receivables },

            // **** Benefits ****
            { id: PrPayslipItemTypeCode.MealVoucherEmployeeDeduction },
            { id: PrPayslipItemTypeCode.RemoteWorkBenefit },
            { id: PrPayslipItemTypeCode.LumpSumMealBenefit },
            { id: PrPayslipItemTypeCode.Illness },

            // **** Non-financial income ****
            { id: PrPayslipItemTypeCode.NonfinancialIncomeTotal },
        ]
    },
    {
        rows: [
            // **** Amount to pay employee ****
            { id: PrPayslipItemTypeCode.AmountToPayEmployee, isHeader: true }
        ]
    },
]);

const vacationBalanceFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    const previous = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.CzVacationPreviouslyUsed)?.IntValue ?? 0;
    const entitlement = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.CzVacationEntitlement)?.IntValue ?? 0;
    const calc = entitlement - previous - empItem.IntValue;
    return valueFormatter ? valueFormatter(calc) : calc.toString();
};

const absenceAmountFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    return valueFormatter ? valueFormatter(empItem.IntValue) : empItem.IntValue?.toString();
};

const sickdayBalanceFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    const previous = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.CzSickDayPreviouslyUsed)?.IntValue ?? 0;
    const entitlement = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.CzSickDayEntitlement)?.IntValue ?? 0;
    const calc = entitlement - previous - empItem.IntValue;
    return valueFormatter ? valueFormatter(calc) : calc.toString();
};

const socialInsuranceBaseFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter, value: TFormTableListValue, args) => {
    const calc = args.employment.Items.find(x => x.TypeCode === PrPayslipEmploymentItemTypeCode.BasisForSocialInsuranceCalculation)?.DecimalValue ?? 0;
    console.log(valueFormatter);
    return valueFormatter ? valueFormatter(calc) : calc.toString();
};


const ExtraSalaryBaseItemTypes = [
    PrPayslipEmploymentItemTypeCode.Night,
    PrPayslipEmploymentItemTypeCode.Weekend,
    PrPayslipEmploymentItemTypeCode.Overtime,
    PrPayslipEmploymentItemTypeCode.Holiday,
];
const extraSalaryCountFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    return valueFormatter ? valueFormatter(empItem.IntValue) : empItem.IntValue?.toString();
};
const extraSalaryPercentageFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const subItem = (item as unknown as IPrPayslipEmploymentItemEntity)?.Subitems?.[0];
    return valueFormatter ? valueFormatter(subItem.DecimalValue) : subItem.DecimalValue?.toString();
};
const extraSalaryAbsValueFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    return valueFormatter ? valueFormatter(empItem.IntValue) : empItem.IntValue?.toString();
};

export const isStartOfEndOfMonth = (date: Date, validType: "ValidFrom" | "ValidTo"): boolean => {
    const fn = validType === "ValidFrom" ? "startOf" : "endOf";
    return getUtcDayjs(date).isSame(getUtcDayjs(date)[fn]("month"), "day");
};

const _hasDifferentValidityDate = (item: TPayslipItem, validType: "ValidFrom" | "ValidTo"): boolean => {
    const subItem = ((item as TPayslipItem).Subitems as TRecordAny[])?.find(i => i.TypeCode?.includes(validType)) as IPrPayslipEmploymentSubitemEntity;
    return !!subItem && !!subItem.DateValue && !isStartOfEndOfMonth(subItem.DateValue, validType);
};

const filterOutValidityColumns: TFilterSubitemsFn = (defs, args) => {
    const hasNonFullMonthValidity = args.filteredItems?.some(item => {
        return _hasDifferentValidityDate(item as TPayslipItem, "ValidFrom") || _hasDifferentValidityDate(item as TPayslipItem, "ValidTo");
    });

    // if there is some subitem with different validity, we filter nothing, otherwise we filter out items which includes ValidFrom/To in id
    if (!hasNonFullMonthValidity) {
        defs = defs?.filter(def => {
            return !((def?.id as string)?.includes("Validity"));
        });
    }

    return defs;
};

const salaryValidityFormatter: TPayslipOverviewFormatterFn = (item) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    const validFrom = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.CzSalaryWorkedValidFrom)?.DateValue;
    const validTo = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.CzSalaryWorkedValidTo)?.DateValue;
    const isValidFromStartOfMonth = validFrom ? isStartOfEndOfMonth(validFrom, "ValidFrom") : true;
    const isValidToEndOfMonth = validTo ? isStartOfEndOfMonth(validTo, "ValidTo") : true;
    if (isValidToEndOfMonth && isValidFromStartOfMonth) {
        return DASH_CHARACTER;
    } else if (!isValidToEndOfMonth && !isValidFromStartOfMonth) {
        return `${DateType.format(validFrom, DateFormat.dayAndMonth)} - ${DateType.format(validTo, DateFormat.dayAndMonth)}`;
    } else if (!isValidFromStartOfMonth) {
        return `${i18next.t("Common:Time.From")} ${DateType.format(validFrom, DateFormat.dayAndMonth)}`;
    }
    return `${i18next.t("Common:Time.To")} ${DateType.format(validTo, DateFormat.dayAndMonth)}`;
};

const workedHoursFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    return empItem.IntValue ? valueFormatter(empItem.IntValue) : DASH_CHARACTER;
};

/**
 * Configuration for basic payslip calculations block related to employment
 */
export const getTotalOverviewEmploymentSectionsDef: () => IPayslipOverviewSectionsDef = () => (
    {
        rows: [
            { id: PrPayslipEmploymentItemTypeCode.PlannedWorkHours, valueType: RowValueType.HoursFromMinutes },
            { id: PrPayslipEmploymentItemTypeCode.AverageSalary, valueType: RowValueType.CurrencyPerHour },

            // **** Salary Components ****
            { id: PrPayslipEmploymentItemTypeCode.GrossSalaryEmployment, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.SalaryForHoursWorked,
                    PrPayslipEmploymentItemTypeCode.SalaryPercentageBonus,
                    PrPayslipEmploymentItemTypeCode.SalaryBonus,
                    PrPayslipEmploymentItemTypeCode.OtherSalaryFullyTaxed,
                ],
                headerRow: {
                    label: getRowHeaderLabel("Name"),
                    value: getRowHeaderLabel("Amount"),
                    isHeader: true,
                    isSubRow: true,
                    subItems: [{
                        id: "Validity",
                        value: getRowHeaderLabel("Validity")
                    }, {
                        id: "Worked",
                        value: getRowHeaderLabel("Worked")
                    }, {
                        value: getRowHeaderLabel("Size")
                    }],
                    filterSubItems: filterOutValidityColumns
                },
                isSubRow: true,
                filterSubItems: filterOutValidityColumns,
                subItems: [
                    {
                        id: "Validity",
                        formatter: salaryValidityFormatter
                    },
                    { formatter: workedHoursFormatter, valueType: RowValueType.HoursFromMinutes },
                    { id: PrPayslipEmploymentSubitemTypeCode.CzSalaryWorkedBase }
                ]
            },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.HolidayCompensation
                ],
                isSubRow: true
            },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.MonthlyCompensation,
                ],
                isSubRow: true,
            },
            // **** Salary Components non-financial ****
            { id: PrPayslipEmploymentItemTypeCode.SalaryInKindFullyTaxedTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.SalaryInKindFullyTaxed
                ],
                headerRow: getHeaderRowDef(["Size"]),
                isSubRow: true,
            },
            // **** Salary Components outside Insurance ****
            { id: PrPayslipEmploymentItemTypeCode.OtherSalaryIncomeTaxOnlyTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.OtherSalaryIncomeTaxOnly,
                    PrPayslipEmploymentItemTypeCode.SeverancePay,
                    PrPayslipEmploymentItemTypeCode.TerminationSettlement,
                    PrPayslipEmploymentItemTypeCode.RetirementBenefit,
                    PrPayslipEmploymentItemTypeCode.CompensationForLossOfMedicalFitnessSect_271ca,
                ],
                headerRow: getHeaderRowDef(["Size"]),
                isSubRow: true,
            },
            // **** Salary Components non-financial outside insurance ****
            { id: PrPayslipEmploymentItemTypeCode.SalaryInKindIncomeTaxOnlyTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.SalaryInKindIncomeTaxOnly
                ],
                headerRow: getHeaderRowDef(["Size"]),
                isSubRow: true,
            },

            // **** Absences ****
            { id: PrPayslipEmploymentItemTypeCode.AbsenceTotal, isHeader: true },
            getHeaderRowDef(["Hours"], [
                PrPayslipEmploymentItemTypeCode.Vacation,
                PrPayslipEmploymentItemTypeCode.SickDay
            ]),
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.Vacation,
                    PrPayslipEmploymentItemTypeCode.SickDay,
                    PrPayslipEmploymentItemTypeCode.CustomAbsence,
                    PrPayslipEmploymentItemTypeCode.LegislativeAbsencePaid,
                    PrPayslipEmploymentItemTypeCode.Illness,
                    PrPayslipEmploymentItemTypeCode.LegislativeAbsenceUnpaid
                ],
                isSubRow: true,
                valueType: RowValueType.Currency,
                subItems: [
                    { formatter: absenceAmountFormatter, valueType: RowValueType.HoursFromMinutes }
                ]
            },
            {
                id: PrPayslipEmploymentItemTypeCode.SickDay,
                isSubRow: true,
                valueType: RowValueType.Currency,
                subItems: [
                    { formatter: absenceAmountFormatter, valueType: RowValueType.Days }
                ]
            },
            // **** Extra Salaries ****
            { id: PrPayslipEmploymentItemTypeCode.ExtraSalaryTotal, isHeader: true },
            {
                ...getHeaderRowDef(["Count", "Value"], [
                    ...ExtraSalaryBaseItemTypes,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryHourlyPercentage,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryHourly,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryShift,
                ]),
                value: getRowHeaderLabel("Amount")
            },
            {
                id: [
                    ...ExtraSalaryBaseItemTypes,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryHourlyPercentage
                ],
                subItems: [
                    { formatter: extraSalaryCountFormatter, valueType: RowValueType.HoursFromMinutes },
                    {
                        id: [
                            PrPayslipEmploymentSubitemTypeCode.CzOvertimeValue,
                            PrPayslipEmploymentSubitemTypeCode.CzNightValue,
                            PrPayslipEmploymentSubitemTypeCode.CzWeekendValue,
                            PrPayslipEmploymentSubitemTypeCode.CzHolidayValue,
                            PrPayslipEmploymentSubitemTypeCode.CzCustHourPercValue,
                        ],
                        formatter: extraSalaryPercentageFormatter,
                        valueType: RowValueType.Percentage
                    }
                ],
                valueType: RowValueType.Currency
            },
            {
                id: PrPayslipEmploymentItemTypeCode.ExtraSalaryHourly,
                subItems: [
                    { formatter: extraSalaryCountFormatter, valueType: RowValueType.HoursFromMinutes },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzCustHourAbsValue,
                        valueType: RowValueType.Currency
                    }
                ],
                valueType: RowValueType.Currency
            },
            {
                id: PrPayslipEmploymentItemTypeCode.ExtraSalaryShift,
                subItems: [
                    { formatter: extraSalaryAbsValueFormatter, valueType: RowValueType.String },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzCustShiftAbsValue,
                        valueType: RowValueType.Currency
                    }
                ],
                valueType: RowValueType.Currency
            },
            // **** Benefits ****
            { id: PrPayslipEmploymentItemTypeCode.BenefitsTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.MealVouchers,
                    PrPayslipEmploymentItemTypeCode.LumpSumMealBenefit,
                ],
                headerRow: getHeaderRowDef(["Contribution", "Unit"]),
                isSubRow: true,
                subItems: [
                    {
                        id: [
                            PrPayslipEmploymentSubitemTypeCode.CzBenefitMealVoEmplr,
                            PrPayslipEmploymentSubitemTypeCode.CzBenefitMealLSEmplr
                        ]
                    },
                    {
                        id: [
                            PrPayslipEmploymentSubitemTypeCode.CzBenefitMealVoDays,
                            PrPayslipEmploymentSubitemTypeCode.CzBenefitMealLSDays,
                        ],
                        valueType: RowValueType.Days
                    }
                ]
            },
            {
                id: PrPayslipEmploymentItemTypeCode.RemoteWorkBenefit,
                isSubRow: true,
                subItems: [
                    { id: PrPayslipEmploymentSubitemTypeCode.CzBenefitRemoteWorkA },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzBenefitRemoteWorkH,
                        valueType: RowValueType.Hours
                    }
                ]
            },
            { id: PrPayslipEmploymentItemTypeCode.OtherBenefit, isSubRow: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.MealVouchersUnderLimit,
                    PrPayslipEmploymentItemTypeCode.LumpSumMealBenefitUnderLimit,
                ]
            },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.MealVouchersOverLimit,
                    PrPayslipEmploymentItemTypeCode.LumpSumMealBenefitOverLimit,
                ]
            },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.BenefitsFullyExempt,
                    PrPayslipEmploymentItemTypeCode.BenefitsExemptToLimitOf1_2Avg_Salary,
                    PrPayslipEmploymentItemTypeCode.BenefitsExemptToLimitOfAvg_Salary,
                    PrPayslipEmploymentItemTypeCode.BenefitsOverLimit,
                    PrPayslipEmploymentItemTypeCode.BenefitsNotExempt,
                ]
            },
            { id: PrPayslipEmploymentItemTypeCode.CompanyCarForPrivateUse },

            { id: PrPayslipEmploymentItemTypeCode.SavingForRetirementTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.PensionRetirementSaving,
                    PrPayslipEmploymentItemTypeCode.LifeRetirementSaving,
                    PrPayslipEmploymentItemTypeCode.DIPBenefit,
                ],
                headerRow: {
                    ...getHeaderRowDef(["Subject"]),
                    label: getRowHeaderLabel("Type")
                },
                isSubRow: true,
                useTypeName: true,
                subItems: [
                    {
                        formatter: (item) => {
                            return (item as IPrPayslipEmploymentItemEntity).RetirementSaving?.RetirementSavingsProduct?.PrRetirementSavingsCompany?.Name;
                        }
                    }
                ]
            },
            { id: PrPayslipEmploymentItemTypeCode.TaxfreeSavingForRetirementTotal },
            { id: PrPayslipEmploymentItemTypeCode.TaxableSavingForRetirementTotal },
            { id: PrPayslipEmploymentItemTypeCode.SocialInsuranceTotal, isHeader: true },
            {
                // header row for Employee and Employer social insurance
                label: getRowHeaderLabel("Name"),
                value: getRowHeaderLabel("Amount"),
                isHeader: true,
                isSubRow: true,
                subItems: [{
                    id: "Base",
                    value: getRowHeaderLabel("Base")
                }, {
                    id: "Size",
                    value: getRowHeaderLabel("Size")
                }, {
                    id: "Sale",
                    value: getRowHeaderLabel("Sale")
                }],
                filterSubItems: filterOutSaleSubitemsIfNotPresent,
                visibleIfContains: [PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployee, PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployer]
            },
            {
                id: PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployee,
                filterSubItems: filterOutSaleSubitemsIfNotPresent,
                subItems: [
                    {
                        formatter: socialInsuranceBaseFormatter,
                        valueType: RowValueType.Currency
                    },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeePerc,
                        valueType: RowValueType.Percentage
                    },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeeRetireeDiscPerc,
                        valueType: RowValueType.Percentage
                    }
                ]
            },
            {
                id: PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployer,
                filterSubItems: filterOutSaleSubitemsIfNotPresent,
                subItems: [
                    {
                        formatter: socialInsuranceBaseFormatter,
                        valueType: RowValueType.Currency
                    },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployerPerc,
                        valueType: RowValueType.Percentage
                    },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeeRetireeDiscPerc,
                        valueType: RowValueType.Percentage
                    }
                ]
            },
        ]
    }
);
